/**
 * /coach/applications — owner-only inbox for coach + internship applications.
 *
 * Tabs by status (new / reviewing / contacted / dismissed) and by role
 * (all / coach / internship). Click a row → modal with the full submission.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGmailComposeUrl } from "@/lib/mail";
import {
    ArrowLeft,
    Loader2,
    Briefcase,
    GraduationCap,
    Mail,
    Phone,
    Linkedin,
    ExternalLink,
    Inbox,
    X,
    Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
    { value: "new", label: "New" },
    { value: "reviewing", label: "Reviewing" },
    { value: "contacted", label: "Contacted" },
    { value: "dismissed", label: "Dismissed" },
];

export default function Applications() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusTab, setStatusTab] = useState("new");
    const [roleTab, setRoleTab] = useState("all");
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (!authLoading && (!user || user.role !== "owner")) {
            navigate("/coach", { replace: true });
        }
    }, [authLoading, user, navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: statusTab };
            if (roleTab !== "all") params.role_interest = roleTab;
            const r = await axios.get(`${API}/coach/applications`, {
                params, withCredentials: true,
            });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load applications", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    }, [statusTab, roleTab]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => ({
        coach: rows.filter((r) => r.role_interest === "coach").length,
        internship: rows.filter((r) => r.role_interest === "internship").length,
    }), [rows]);

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div data-testid="applications-page" className="min-h-screen bg-brand-obsidian text-white">
            <section className="pt-28 pb-6 border-b border-zinc-900">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    <button type="button" onClick={() => navigate("/coach")} className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </button>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> Coach applications
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                        Inbox · {STATUSES.find((s) => s.value === statusTab)?.label}
                    </h1>
                    <p className="mt-3 text-sm text-zinc-300">
                        Applications from the public Careers page. Separate from leads.
                    </p>

                    {/* Tabs */}
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        {STATUSES.map((s) => {
                            const on = s.value === statusTab;
                            return (
                                <button
                                    key={s.value}
                                    type="button"
                                    data-testid={`apps-tab-${s.value}`}
                                    onClick={() => setStatusTab(s.value)}
                                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
                                        on ? "border-brand-red text-brand-red bg-brand-red/5"
                                            : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            );
                        })}
                        <div className="w-px h-6 bg-zinc-800 mx-2 hidden sm:block" />
                        {[
                            { value: "all", label: `All (${rows.length})` },
                            { value: "coach", label: `Coach (${counts.coach})` },
                            { value: "internship", label: `Intern (${counts.internship})` },
                        ].map((r) => {
                            const on = r.value === roleTab;
                            return (
                                <button
                                    key={r.value}
                                    type="button"
                                    data-testid={`apps-role-${r.value}`}
                                    onClick={() => setRoleTab(r.value)}
                                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
                                        on ? "border-brand-navy-500 text-brand-navy-200 bg-brand-navy-900/40"
                                            : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-6xl mx-auto px-6 md:px-10">
                    {rows.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-12 text-center text-zinc-300">
                            <Inbox className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                            No applications in this view.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {rows.map((row) => (
                                <ApplicationRow key={row.application_id} row={row} onClick={() => setActive(row)} />
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {active && (
                <ApplicationDetail
                    row={active}
                    onClose={() => setActive(null)}
                    onChanged={async () => { setActive(null); await load(); }}
                />
            )}
        </div>
    );
}

function ApplicationRow({ row, onClick }) {
    const isCoach = row.role_interest === "coach";
    const Icon = isCoach ? Briefcase : GraduationCap;
    const fullName = `${row.first_name} ${row.last_name}`.trim();
    const when = row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
    return (
        <li>
            <button
                type="button"
                data-testid={`app-row-${row.application_id}`}
                onClick={onClick}
                className="w-full text-left border border-zinc-800 rounded-xl bg-zinc-950/60 hover:border-brand-red transition-colors px-5 py-4 flex items-center gap-4"
            >
                <div className="shrink-0 w-10 h-10 rounded-full bg-brand-red/15 border border-brand-red/40 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-brand-red" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-lg tracking-wider text-white truncate">{fullName}</div>
                    <div className="text-xs text-zinc-300 mt-0.5 truncate">
                        {isCoach
                            ? `${row.years_experience ?? "?"}y exp${row.current_employer ? ` · ${row.current_employer}` : ""}${row.availability ? ` · ${row.availability.replace("_", " ")}` : ""}`
                            : `${row.school || "—"}${row.grad_year ? ` · '${String(row.grad_year).slice(-2)}` : ""}${row.hours_per_week ? ` · ${row.hours_per_week}h/wk` : ""}`}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{isCoach ? "Coach" : "Intern"}</div>
                    <div className="text-[11px] text-zinc-500 mt-1">{when}</div>
                </div>
            </button>
        </li>
    );
}

function ApplicationDetail({ row, onClose, onChanged }) {
    const composeUrl = useGmailComposeUrl();
    const [busy, setBusy] = useState(null);
    const isCoach = row.role_interest === "coach";

    const setStatus = async (newStatus) => {
        setBusy(newStatus);
        try {
            await axios.patch(`${API}/coach/applications/${row.application_id}`, { status: newStatus }, { withCredentials: true });
            toast.success(`Moved to ${newStatus}`);
            onChanged();
        } catch (e) {
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        } finally { setBusy(null); }
    };

    const remove = async () => {
        if (!window.confirm("Permanently delete this application?")) return;
        setBusy("delete");
        try {
            await axios.delete(`${API}/coach/applications/${row.application_id}`, { withCredentials: true });
            toast.success("Deleted");
            onChanged();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="application-detail" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-3xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">{isCoach ? "Coach" : "Internship"}</div>
                        <h3 className="font-display text-2xl tracking-wider uppercase text-white">{row.first_name} {row.last_name}</h3>
                    </div>
                    <button type="button" onClick={onClose} data-testid="application-close" className="text-zinc-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <a href={composeUrl(row.email)} target="_blank" rel="noopener noreferrer" title={composeUrl.title} className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red">
                            <Mail className="w-4 h-4 text-brand-red" /> {row.email}
                        </a>
                        <a href={`tel:${row.phone}`} className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red">
                            <Phone className="w-4 h-4 text-brand-red" /> {row.phone}
                        </a>
                        {row.linkedin_url && (
                            <a href={row.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red truncate">
                                <Linkedin className="w-4 h-4 text-brand-red" /> LinkedIn <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        {row.resume_url && (
                            <a href={row.resume_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-zinc-200 hover:text-brand-red truncate">
                                Resume <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>

                    <Block title="Role-specific">
                        {isCoach ? (
                            <ul className="space-y-1.5 text-sm text-zinc-300">
                                <li><strong className="text-white">Experience:</strong> {row.years_experience ?? "?"} years</li>
                                {row.current_employer && <li><strong className="text-white">Current employer:</strong> {row.current_employer}</li>}
                                {row.availability && <li><strong className="text-white">Availability:</strong> {row.availability.replace("_", " ")}</li>}
                                {row.current_certs?.length > 0 && (
                                    <li><strong className="text-white">Certs:</strong> {row.current_certs.join(" · ")}</li>
                                )}
                            </ul>
                        ) : (
                            <ul className="space-y-1.5 text-sm text-zinc-300">
                                <li><strong className="text-white">School:</strong> {row.school || "—"}</li>
                                {row.grad_year && <li><strong className="text-white">Graduation:</strong> {row.grad_year}</li>}
                                {row.hours_per_week && <li><strong className="text-white">Hours/wk:</strong> {row.hours_per_week}</li>}
                                {row.compensation_pref && <li><strong className="text-white">Comp pref:</strong> {row.compensation_pref}</li>}
                            </ul>
                        )}
                        {row.earliest_start && <p className="text-sm text-zinc-300 mt-2"><strong className="text-white">Earliest start:</strong> {row.earliest_start}</p>}
                    </Block>

                    <Block title="Why MaxSC">
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{row.why_maxsc}</p>
                    </Block>

                    {row.philosophy && Object.values(row.philosophy).some(Boolean) && (
                        <Block title="Philosophy answers">
                            <ul className="space-y-3 text-sm">
                                {Object.entries(row.philosophy).map(([k, v]) => v && (
                                    <li key={k}>
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{k.replace(/_/g, " ")}</div>
                                        <div className="text-zinc-200 mt-0.5">{v}</div>
                                    </li>
                                ))}
                            </ul>
                        </Block>
                    )}

                    {row.cueing_picks?.length > 0 && (
                        <Block title="Cueing picks">
                            <ul className="space-y-1 text-sm text-zinc-200 list-disc list-inside">
                                {row.cueing_picks.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                        </Block>
                    )}

                    {row.scenario_response && (
                        <Block title="First session with a 14-year-old">
                            <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{row.scenario_response}</p>
                        </Block>
                    )}

                    {/* Actions */}
                    <div className="pt-4 border-t border-zinc-800 flex flex-wrap gap-2">
                        {STATUSES.filter((s) => s.value !== row.status).map((s) => (
                            <button
                                key={s.value}
                                type="button"
                                data-testid={`app-set-${s.value}`}
                                disabled={!!busy}
                                onClick={() => setStatus(s.value)}
                                className="btn-ghost-tactical text-xs"
                            >
                                {busy === s.value ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                Mark {s.label}
                            </button>
                        ))}
                        <div className="flex-1" />
                        <button
                            type="button"
                            data-testid="app-delete"
                            disabled={!!busy}
                            onClick={remove}
                            className="text-xs tracking-[0.15em] uppercase px-3 py-2 border border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red transition-colors inline-flex items-center gap-1.5"
                        >
                            {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">{title}</div>
            {children}
        </div>
    );
}
